import classNames from 'classnames'
import {
  ComponentProps,
  CSSProperties,
  forwardRef,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import { IconFa, Menu, MenuButton, MenuItems, MenuProps } from '../../atoms'
import styles from './ActionBar.module.css'
import { useElementDimensions, useElementSize } from './use-element-size'
import { ActionBarActionProps, getActionsFromChildren } from './utils'

interface ActionBarStatusProps {
  children: ReactNode
}

/**
 * Used to display the current status of the action-bar, f.ex. the current number of selected
 * items in a list.
 */
export const ActionBarStatus = ({ children }: ActionBarStatusProps) => {
  return <div className={styles.status}>{children}</div>
}

export const ActionBarAction = forwardRef<HTMLButtonElement, ActionBarActionProps>(
  ({ icon, className: propClassName, children, ...others }, ref) => {
    const className = classNames(styles.action, propClassName, others.disabled && styles.disabled)

    return (
      <button ref={ref} type="button" className={classNames(className, styles['action-bar-action'])} {...others}>
        {icon && (
          <div className={styles.actionIcon}>
            <IconFa icon={icon} />
          </div>
        )}
        <div className={classNames(styles['inner-content'], 'flex')}>{children}</div>
      </button>
    )
  }
)

interface ActionBarActionWrapperProps {
  index: number
  isVisible: boolean
  children: ReactNode
  onRefChange(index: number, ref: HTMLDivElement | null): void
}

const ActionBarActionWrapper = ({ index, isVisible, children, onRefChange }: ActionBarActionWrapperProps) => {
  const handleRef = useCallback((element: HTMLDivElement | null) => {
    onRefChange(index, element)
  }, [])

  return (
    <div ref={handleRef} className={isVisible ? 'visible' : 'invisible'}>
      {children}
    </div>
  )
}

const ACTION_GAP = 8

interface ActionBarActionsProps {
  /**
   * children should be either ActionBarAction elements or React.Fragment
   */
  children: ReactNode
  /**
   * Called with how many actions fit in the bar. Pass the setter from useHiddenActions to show the rest elsewhere.
   */
  onVisibleCountChange?: (visibleCount: number) => void
  style?: CSSProperties
  className?: string
}

export const ActionBarActions = ({ children, style, className, onVisibleCountChange }: ActionBarActionsProps) => {
  const actions = useMemo(() => getActionsFromChildren(children), [children])
  // we can't use a ref to save the elements as we need the useElementSize hook to rerender when
  // the elements change.
  const [container, setContainer] = useState<HTMLDivElement | null>(null)
  // as the order of ref callbacks isn't guaranteed to be in the same order as they are rendered
  // we same them in a map indexed by their index in the actions array
  const [actionElements, setActionElements] = useState<Map<number, Element>>(new Map())
  const actionElementsArray = useMemo(
    () =>
      Array.from(actionElements)
        .sort(([a], [b]) => a - b)
        .map(([, element]) => element),
    [actionElements]
  )

  const containerSize = useElementSize(container)
  const dimensions = useElementDimensions(actionElementsArray)

  const visibleActionIndexes = useMemo(() => {
    const elementRects = actionElementsArray
      .map((element) => dimensions.get(element))
      .filter((value): value is DOMRect => Boolean(value))

    // if we don't have all element sizes yet display no actions
    if (!containerSize || elementRects.length !== actions.length) {
      return []
    }

    const availableWidth = containerSize.width - ACTION_GAP * (actionElementsArray.length - 1)

    const [, visibleIndexes] = elementRects.reduce<[number, number[]]>(
      ([remainingSize, visibleIndexes], rect, index) => {
        const { width } = rect

        // if the element fits in the current available space include it in the
        // visible indexes array and remove the width from the available width.
        if (width <= remainingSize) {
          return [remainingSize - width, [...visibleIndexes, index]]
        }

        // if an element doesn't fit return 0 as the available width to prevent
        // other actions that appears later in the array to be included.
        return [0, visibleIndexes]
      },
      [availableWidth, []]
    )

    return visibleIndexes
  }, [containerSize, dimensions, actionElementsArray])

  const handleSetActionRef = useCallback(
    (index: number, element: Element | null) => {
      setActionElements((current) => {
        const updated = new Map(current)

        if (!element) {
          updated.delete(index)
        } else {
          updated.set(index, element)
        }

        return updated
      })
    },
    [actionElements]
  )

  useEffect(() => {
    onVisibleCountChange?.(visibleActionIndexes.length)
  }, [visibleActionIndexes.length])

  return (
    <div className={classNames(styles.actions, className)} style={style}>
      <div ref={setContainer} className={styles.actionsInner}>
        {actions.map((element: ReactElement<ActionBarActionProps>, index) => (
          <ActionBarActionWrapper
            key={index}
            index={index}
            isVisible={visibleActionIndexes.includes(index)}
            onRefChange={handleSetActionRef}
          >
            {element}
          </ActionBarActionWrapper>
        ))}
      </div>
    </div>
  )
}

/**
 * The actions that ActionBarActions could not fit, taken from the same children on every render so that they carry
 * the current props and handlers. Pass onVisibleCountChange to ActionBarActions.
 */
export const useHiddenActions = (children: ReactNode) => {
  const [visibleCount, setVisibleCount] = useState<number>()
  const hiddenActions = visibleCount === undefined ? [] : getActionsFromChildren(children).slice(visibleCount)
  return { hiddenActions, onVisibleCountChange: setVisibleCount }
}

interface ActionBarSecondaryActionsProps extends ComponentProps<'div'> {
  children: ReactNode
}

/**
 * Used to display secondary actions that are always visible on the right side of the action bar
 */
export const ActionBarSecondaryActions = ({ children, className: propClassName }: ActionBarSecondaryActionsProps) => {
  const className = classNames(styles.secondaryActions, propClassName)

  return (
    <div className={className}>
      <div className={styles.divider} />
      {children}
    </div>
  )
}

interface ActionBarSecondaryActionsMenuProps extends Omit<MenuProps, 'placement' | 'children'> {
  buttonLabel: string
  openInPortal?: boolean
  children: ReactNode
  menuClassName?: string
}

export const ActionBarSecondaryActionsMenu = ({
  buttonLabel,
  children,
  openInPortal,
  menuClassName,
  ...other
}: ActionBarSecondaryActionsMenuProps) => (
  <Menu
    openInPortal={openInPortal}
    placement="top-end"
    {...other}
    positionStrategy={openInPortal ? 'fixed' : 'absolute'}
  >
    <MenuButton>
      {({ open }) => (
        <div
          className={classNames(
            open ? 'border-primary-500' : 'border-gray-300',
            'flex justify-between items-center h-11 min-w-[242px] px-4 border-solid border rounded-xs gap-3'
          )}
        >
          <span className="text-sm text-gray-600">{buttonLabel}</span>
          <IconFa
            icon={open ? ['far', 'chevron-up'] : ['far', 'chevron-down']}
            className={open ? 'text-gray-500' : 'text-primary-500'}
          />
        </div>
      )}
    </MenuButton>

    <MenuItems containerClassName="z-1000" className={classNames(menuClassName, styles['secondary-action-menu'])}>
      {children}
    </MenuItems>
  </Menu>
)

interface ActionBarProps extends ComponentProps<'div'> {
  bottomOffset?: number
  children: ReactNode
}

export const ActionBar = ({
  children,
  bottomOffset = 0,
  className: propClassName,
  style,
  ...props
}: ActionBarProps) => {
  const className = classNames(styles.bar, propClassName)

  return (
    <div style={{ bottom: bottomOffset, ...style }} className={className} {...props}>
      {children}
    </div>
  )
}
